import { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatTime } from '@easyedit/timeline'
import { api, ApiError } from '../../../lib/api/client'
import type { MediaAsset, Project, TranscriptResponse } from '../../../lib/api/types'
import { useEditorStore } from '../../../stores/editorStore'

interface TranscriptionSummary {
  id: string
  assetId: string
  status: string
  provider: string
  _count: { words: number; speakers: number }
}

/**
 * Edição por texto.
 *
 * Selecionar palavras e apagar remove o trecho correspondente da timeline — de forma não
 * destrutiva e desfazível. É o recurso que muda o modelo mental do editor: quem sabe
 * escrever sabe editar vídeo.
 */
export function TranscriptPanel({ project, assets }: { project: Project; assets: MediaAsset[] }) {
  const queryClient = useQueryClient()
  const sequence = useEditorStore((s) => s.sequence)
  const playhead = useEditorStore((s) => s.playhead)
  const setPlayhead = useEditorStore((s) => s.setPlayhead)
  const [selection, setSelection] = useState<Set<number>>(new Set())
  const [anchor, setAnchor] = useState<number | null>(null)

  const list = useQuery({
    queryKey: ['transcriptions', project.id],
    queryFn: () => api.get<TranscriptionSummary[]>(`/projects/${project.id}/transcriptions`),
  })

  const completed = list.data?.find((t) => t.status === 'COMPLETED')

  const transcript = useQuery({
    queryKey: ['transcript', project.id, completed?.id],
    queryFn: () => api.get<TranscriptResponse>(`/transcriptions/${completed!.id}`),
    enabled: Boolean(completed),
  })

  const transcribable = assets.filter((a) => a.hasAudio && a.status === 'READY')
  const importRef = useRef<HTMLInputElement>(null)

  /**
   * Importar legenda pronta.
   *
   * Sem isso, um usuário sem chave de provedor fica sem transcrição — e sem transcrição
   * metade das ferramentas de IA não funciona. Muita gente já tem o SRT do material, e
   * importar é mais rápido e mais barato que transcrever de novo.
   */
  const importSubtitles = useMutation({
    mutationFn: async ({ file, assetId }: { file: File; assetId: string }) => {
      const content = await file.text()
      const format = file.name.toLowerCase().endsWith('.vtt')
        ? 'vtt'
        : file.name.toLowerCase().endsWith('.json')
          ? 'json'
          : 'srt'
      return api.post(`/projects/${project.id}/transcriptions/import`, {
        assetId,
        content,
        format,
        language: 'pt',
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['transcriptions', project.id] })
      void queryClient.invalidateQueries({ queryKey: ['transcript', project.id] })
    },
    onError: (error) =>
      useEditorStore.getState().setError(
        error instanceof ApiError ? error.message : 'Não foi possível importar a legenda',
      ),
  })

  const transcribe = useMutation({
    mutationFn: (assetId: string) =>
      api.post(`/projects/${project.id}/transcribe`, { assetId, language: 'pt', diarize: true }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['transcriptions', project.id] }),
    onError: (error) =>
      useEditorStore.getState().setError(
        error instanceof ApiError ? error.message : 'Não foi possível transcrever',
      ),
  })

  const removeWords = useMutation({
    mutationFn: (wordIndices: number[]) =>
      api.post<{ removedWords: number; cuts: number; removedSeconds: number }>(
        `/sequences/${sequence!.id}/transcript-edit`,
        { transcriptionId: completed!.id, wordIndices },
      ),
    onSuccess: async (result) => {
      setSelection(new Set())
      const fresh = await api.get<{ sequence: NonNullable<typeof sequence> }>(`/sequences/${sequence!.id}`)
      useEditorStore.getState().load(fresh.sequence, project.id)
      useEditorStore.getState().setError(null)
      void queryClient.invalidateQueries({ queryKey: ['transcript', project.id] })
      void result
    },
  })

  const words = transcript.data?.words ?? []

  // A palavra sob o playhead — dá a sensação de "seguir a fala" ao reproduzir.
  const activeIndex = useMemo(
    () => words.findIndex((w) => playhead >= w.start && playhead < w.end),
    [words, playhead],
  )

  function clickWord(index: number, event: React.MouseEvent) {
    if (event.shiftKey && anchor !== null) {
      // Shift seleciona um intervalo — apagar uma frase inteira precisa ser um gesto só.
      const [from, to] = anchor < index ? [anchor, index] : [index, anchor]
      const next = new Set(selection)
      for (let i = from; i <= to; i++) next.add(i)
      setSelection(next)
      return
    }
    if (event.metaKey || event.ctrlKey) {
      const next = new Set(selection)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      setSelection(next)
      setAnchor(index)
      return
    }
    setSelection(new Set([index]))
    setAnchor(index)
    setPlayhead(words[index]!.start)
  }

  if (!completed) {
    return (
      <div className="p-3 flex flex-col gap-3">
        <h2 className="text-xs font-semibold">Transcrição</h2>
        <p className="text-2xs text-ink-400 leading-relaxed">
          Transcreva para liberar edição por texto, remoção de silêncio, legendas e Auto Edit.
        </p>
        {transcribable.length === 0 ? (
          <p className="text-2xs text-ink-500">Envie um arquivo com áudio primeiro.</p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {transcribable.map((asset) => (
              <button
                key={asset.id}
                className="btn-outline justify-between py-2"
                onClick={() => transcribe.mutate(asset.id)}
                disabled={transcribe.isPending}
              >
                <span className="truncate">{asset.filename}</span>
                <span className="font-mono text-2xs text-ink-500 shrink-0">
                  {asset.durationSec?.toFixed(0)}s
                </span>
              </button>
            ))}
          </div>
        )}
        {list.data?.some((t) => t.status === 'QUEUED' || t.status === 'PROCESSING') && (
          <p className="text-2xs text-accent">Transcrevendo… isso aparece aqui quando terminar.</p>
        )}
        {list.data?.some((t) => t.status === 'FAILED') && (
          <p className="text-2xs text-danger leading-relaxed">
            A transcrição falhou. Configure um provedor (OPENAI_API_KEY ou whisper.cpp) ou
            importe uma legenda abaixo.
          </p>
        )}

        <div className="border-t border-ink-750 pt-3">
          <h3 className="label mb-1">Já tem legenda?</h3>
          <p className="text-2xs text-ink-500 leading-relaxed mb-2">
            Importe um SRT, VTT ou JSON e libere todas as ferramentas de IA na hora, sem
            custo e sem provedor configurado.
          </p>
          <button
            className="btn-outline w-full justify-center py-2"
            onClick={() => importRef.current?.click()}
            disabled={importSubtitles.isPending || transcribable.length === 0}
          >
            {importSubtitles.isPending ? 'Importando…' : 'Importar SRT / VTT'}
          </button>
          <input
            id="transcript-import"
            ref={importRef}
            type="file"
            accept=".srt,.vtt,.json,text/plain"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0]
              const assetId = transcribable[0]?.id
              if (file && assetId) importSubtitles.mutate({ file, assetId })
              event.target.value = ''
            }}
          />
          {transcribable.length === 0 && (
            <p className="mt-1 text-2xs text-ink-500">
              Envie primeiro o arquivo de vídeo ou áudio correspondente.
            </p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-ink-750 shrink-0">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-xs font-semibold">Transcrição</h2>
          <span className="font-mono text-2xs text-ink-500 tnum">{words.length} palavras</span>
        </div>
        <p className="mt-1 text-2xs text-ink-500 leading-relaxed">
          Clique para navegar. Shift para selecionar um trecho. Apagar remove da timeline.
        </p>
        {selection.size > 0 && (
          <button
            className="btn-danger w-full justify-center mt-2 py-1.5"
            onClick={() => removeWords.mutate([...selection].sort((a, b) => a - b))}
            disabled={removeWords.isPending || !sequence}
          >
            {removeWords.isPending
              ? 'Removendo…'
              : `Remover ${selection.size} palavra${selection.size > 1 ? 's' : ''} do vídeo`}
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-3 text-xs leading-7">
        {words.map((word, index) => {
          const isSelected = selection.has(index)
          const isActive = index === activeIndex
          return (
            <span
              key={index}
              onClick={(event) => clickWord(index, event)}
              className={`cursor-pointer rounded px-0.5 transition-colors
                ${isSelected ? 'bg-danger/30 text-danger line-through' : ''}
                ${isActive && !isSelected ? 'bg-accent/25 text-accent' : ''}
                ${!isSelected && !isActive ? 'hover:bg-ink-750' : ''}`}
              title={`${formatTime(word.start)} · confiança ${(word.confidence * 100).toFixed(0)}%`}
            >
              {word.text}{' '}
            </span>
          )
        })}
      </div>
    </div>
  )
}
